import { useState, type ReactNode } from 'react';
import { authClient } from '@/auth';
import { PlaeeClient, type PlaeeAuthBundle } from './plaee-client';
import { RealtimeManager } from './realtime-manager';
import { RealtimeManagerCtx, type RealtimeManagerContextValue } from './realtime-manager-ctx';

function resolvePlaeeWsUrl(): string {
  const fromEnv = import.meta.env.VITE_PLAEE_CENTRIFUGO_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  throw new Error('VITE_PLAEE_CENTRIFUGO_URL is not configured');
}

interface RealtimeAuthResponse {
  token: string;
  customerId?: string | number;
}

async function fetchPlaeeAuth(): Promise<PlaeeAuthBundle> {
  const headers = (authClient.getAuthHeaders() as Record<string, string | undefined> | undefined) ?? {};
  const authorization = headers.Authorization ?? headers.authorization;
  if (!authorization) return { token: null, plaeeCustomerId: null };

  const base = import.meta.env.VITE_API_URL ?? '';
  const res = await fetch(`${base}/prediction-market/realtime/auth`, {
    method: 'POST',
    headers: { Authorization: authorization },
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 404) return { token: null, plaeeCustomerId: null };
    throw new Error(`realtime/auth failed: ${res.status}`);
  }
  const body = (await res.json()) as RealtimeAuthResponse;
  const plaeeCustomerId = body.customerId === undefined || body.customerId === null ? null : String(body.customerId);
  return { token: body.token, plaeeCustomerId };
}

// Module-level singleton so React StrictMode's double-invoked `useState`
// initializer (and HMR re-runs) reuse the same PlaeeClient + RealtimeManager
// instead of creating duplicates that race for the upstream Centrifuge
// connection and diverge from `window.__realtimeManager`.
let SINGLETON: RealtimeManagerContextValue | null = null;

function getSingleton(): RealtimeManagerContextValue {
  if (SINGLETON !== null) return SINGLETON;
  const client = new PlaeeClient({
    url: resolvePlaeeWsUrl(),
    getAuth: fetchPlaeeAuth,
    onError: (err) => console.warn('[plaee-client] error', err),
  });
  const manager = new RealtimeManager({ client });
  if (import.meta.env.DEV) {
    // Dev-only: poke at active subscriptions from devtools without threading
    // the manager through component refs. e.g.
    //   __realtimeManager.getActiveChannels()
    // Mirrors the `__realtimeBus` handle in RealtimePrivateBridge.
    (globalThis as unknown as { __realtimeManager?: RealtimeManager; __plaeeClient?: PlaeeClient }).__realtimeManager =
      manager;
    (globalThis as unknown as { __realtimeManager?: RealtimeManager; __plaeeClient?: PlaeeClient }).__plaeeClient = client;
  }
  SINGLETON = { client, manager };
  return SINGLETON;
}

/**
 * Mounts a singleton {@link PlaeeClient} + {@link RealtimeManager} for the app.
 * Provide near the root, above any feature that consumes realtime hooks.
 */
export function RealtimeManagerProvider({ children }: { children?: ReactNode }): ReactNode {
  const [value] = useState<RealtimeManagerContextValue>(getSingleton);
  return <RealtimeManagerCtx.Provider value={value}>{children}</RealtimeManagerCtx.Provider>;
}
